import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "../api";
import { Button, Card, CardContent, Input, Label, Select, Switch } from "../components/ui";

const LABELS = {
  FIRST_CALL: "10 minutos antes",
  SECOND_CALL: "5 minutos antes",
  FINAL_CALL: "Na largada",
};

export default function Settings() {
  const [draft, setDraft] = useState(null);
  const load = async () => {
    try { const r = await api.get("/settings"); setDraft(r.data); } catch { }
  };
  useEffect(() => { load(); }, []);

  if (!draft) return <div className="p-6 text-gray-500">Carregando...</div>;

  const setTts = (patch) => setDraft((d) => ({ ...d, tts: { ...d.tts, ...patch } }));
  const setTrigger = (k, patch) => setDraft((d) => ({ ...d, triggers: { ...d.triggers, [k]: { ...d.triggers[k], ...patch } } }));

  const save = async () => {
    try {
      await api.put("/settings", {
        polling_interval_seconds: Number(draft.polling_interval_seconds),
        polling_url: draft.polling_url,
        grouping_mode: draft.grouping_mode,
        triggers: draft.triggers,
        tts: draft.tts,
        enabled: draft.enabled,
        timezone: draft.timezone,
      });
      toast.success("Configurações salvas");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <h1 className="font-display text-4xl uppercase leading-none">Configurações</h1>
      <Card>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Sistema ativo</Label>
              <div className="flex items-center gap-2 mt-2">
                <Switch data-testid="settings-enabled" checked={!!draft.enabled} onChange={(v) => setDraft((d) => ({ ...d, enabled: v }))} />
                <span className="text-sm text-gray-400">{draft.enabled ? "Polling ativo" : "Pausado"}</span>
              </div>
            </div>
            <div>
              <Label>Intervalo de polling</Label>
              <Select data-testid="polling-interval" value={draft.polling_interval_seconds || 10} onChange={(e) => setDraft((d) => ({ ...d, polling_interval_seconds: Number(e.target.value) }))}>
                {[5, 10, 15, 30, 60].map((v) => <option key={v} value={v}>{v} segundos</option>)}
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>URL da fonte de dados</Label>
              <Input data-testid="polling-url" value={draft.polling_url || ""} onChange={(e) => setDraft((d) => ({ ...d, polling_url: e.target.value }))} />
            </div>
            <div>
              <Label>Agrupamento</Label>
              <Select data-testid="grouping" value={draft.grouping_mode || "GROUP_ALL"} onChange={(e) => setDraft((d) => ({ ...d, grouping_mode: e.target.value }))}>
                <option value="GROUP_ALL">Agrupar todos os pilotos</option>
                <option value="INDIVIDUAL">Chamar individualmente</option>
              </Select>
            </div>
            <div>
              <Label>Fuso horário</Label>
              <Input value={draft.timezone || ""} onChange={(e) => setDraft((d) => ({ ...d, timezone: e.target.value }))} />
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold uppercase tracking-widest mb-2">Chamadas automáticas</div>
            {["FIRST_CALL", "SECOND_CALL", "FINAL_CALL"].map((k) => (
              <div key={k} className="flex items-center gap-3 p-3 border border-panel mb-1">
                <Switch data-testid={`trigger-${k}`} checked={!!draft.triggers?.[k]?.enabled} onChange={(v) => setTrigger(k, { enabled: v })} />
                <div className="flex-1">
                  <div className="text-sm">{LABELS[k]}</div>
                  <div className="text-xs text-gray-500">{draft.triggers?.[k]?.offset_minutes ?? 0} min antes da largada</div>
                </div>
                <Input type="number" min={0} max={60} value={draft.triggers?.[k]?.offset_minutes ?? 0}
                  onChange={(e) => setTrigger(k, { offset_minutes: Number(e.target.value) })}
                  className="w-20" />
              </div>
            ))}
          </div>

          <div>
            <div className="text-sm font-semibold uppercase tracking-widest mb-2">Text-to-Speech (OpenAI)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Voz</Label>
                <Select data-testid="tts-voice" value={draft.tts?.voice || "onyx"} onChange={(e) => setTts({ voice: e.target.value })}>
                  {["alloy","ash","ballad","coral","echo","fable","nova","onyx","sage","shimmer","verse"].map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
              </div>
              <div>
                <Label>Modelo</Label>
                <Select data-testid="tts-model" value={draft.tts?.model || "gpt-4o-mini-tts"} onChange={(e) => setTts({ model: e.target.value })}>
                  <option value="gpt-4o-mini-tts">gpt-4o-mini-tts (pt-BR + estilo)</option>
                  <option value="tts-1">tts-1 (rápido, sotaque EN)</option>
                  <option value="tts-1-hd">tts-1-hd (HD, sotaque EN)</option>
                </Select>
              </div>
              <div>
                <Label>Velocidade</Label>
                <Input type="number" step="0.05" min="0.5" max="2.0" value={draft.tts?.speed ?? 1.0} onChange={(e) => setTts({ speed: Number(e.target.value) })} />
              </div>
            </div>
            {(draft.tts?.model || "gpt-4o-mini-tts") === "gpt-4o-mini-tts" && (
              <div className="mt-4">
                <Label>Instruções de estilo (opcional — só p/ gpt-4o-mini-tts)</Label>
                <textarea
                  data-testid="tts-instructions"
                  rows={4}
                  className="w-full mt-1 bg-black/30 border border-panel rounded-sm text-sm p-3 font-mono"
                  placeholder="Deixe em branco para usar o padrão: locutor esportivo brasileiro, tom enérgico, sotaque neutro paulista, ritmo controlado."
                  value={draft.tts?.instructions || ""}
                  onChange={(e) => setTts({ instructions: e.target.value })}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Descreva a persona: sotaque, ritmo, emoção, contexto. Ex: <em>"locutor de rally, agressivo, sotaque gaúcho, ênfase nos nomes"</em>.
                </div>
              </div>
            )}
            <VoicePreview draft={draft} />
          </div>

          <Button data-testid="settings-save" onClick={save}>Salvar configurações</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function VoicePreview({ draft }) {
  const [text, setText] = useState("Atenção, Rafael Silva e Juliana Costa. Sua sessão será iniciada às dezenove e trinta.");
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  const play = async () => {
    setLoading(true); setAudioUrl(null);
    try {
      const r = await api.post("/tts/preview", {
        text,
        voice: draft.tts?.voice,
        model: draft.tts?.model,
        instructions: draft.tts?.instructions || "",
        speed: draft.tts?.speed || 1.0,
        format: draft.tts?.format || "mp3",
      });
      const url = r.data.audio_url;
      const full = url.startsWith("http") ? url : `${window.location.origin}${url}`;
      setAudioUrl(full);
      // auto-play
      setTimeout(() => { const el = document.getElementById("voice-preview-audio"); if (el) el.play().catch(()=>{}); }, 100);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-4 p-4 border border-panel rounded-sm bg-black/20">
      <div className="text-xs uppercase tracking-widest text-gray-400 mb-2">Testar voz</div>
      <textarea
        data-testid="tts-preview-text"
        rows={2}
        className="w-full bg-black/30 border border-panel rounded-sm text-sm p-2"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex items-center gap-3 mt-3">
        <Button data-testid="tts-preview-btn" onClick={play} disabled={loading}>
          {loading ? "Gerando..." : "Reproduzir prévia"}
        </Button>
        {audioUrl && <audio id="voice-preview-audio" controls src={audioUrl} className="flex-1 h-9" />}
      </div>
    </div>
  );
}
